import { useCallback, useRef, useState } from 'react';

let storageWarned = false;

function isStorageAvailable(): boolean {
  try {
    const probe = '__fieldiq_ls_probe__';
    window.localStorage.setItem(probe, probe);
    window.localStorage.removeItem(probe);
    return true;
  } catch {
    return false;
  }
}

export function useLocalStorage<T>(
  key: string,
  defaultValue: T,
): [T, (next: T | ((prev: T) => T)) => void] {
  const available = useRef<boolean>(isStorageAvailable());

  const [value, setValue] = useState<T>(() => {
    if (!available.current) return defaultValue;
    try {
      const raw = window.localStorage.getItem(key);
      if (raw === null) return defaultValue;
      return JSON.parse(raw) as T;
    } catch {
      return defaultValue;
    }
  });

  const update = useCallback(
    (next: T | ((prev: T) => T)) => {
      setValue((prev) => {
        const resolved =
          typeof next === 'function' ? (next as (p: T) => T)(prev) : next;
        if (available.current) {
          try {
            window.localStorage.setItem(key, JSON.stringify(resolved));
          } catch {
            if (!storageWarned) {
              storageWarned = true;
              console.warn(
                '[useLocalStorage] write failed — falling back to in-memory state',
              );
            }
          }
        } else if (!storageWarned) {
          storageWarned = true;
          console.warn(
            '[useLocalStorage] localStorage unavailable — using in-memory state only',
          );
        }
        return resolved;
      });
    },
    [key],
  );

  return [value, update];
}
